/* eslint-disable max-classes-per-file -- we only use classes as Error value objects */
import type { z } from 'zod';
import { match, P } from 'ts-pattern';
import { identity, tryCatchAsync } from '~/utilities/util';
import { isString } from './type-guards';

type FetchParams = Parameters<typeof fetch>;

export type JSONResult<T> = {
  response: Response;
  object: T;
};

type FetchErrorResponse =
  | {
      type: 'fetchError';
      message: string;
      error: Error;
      fetchResponse?: never;
    }
  | {
      type: 'fetchErrorWithResponse';
      message: string;
      error: FetchHTTPError;
      fetchResponse: Response;
    };

type SchemaErrorResponse = {
  type: 'schemaError';
  message: string;
  error: z.ZodError;
  fetchResponse: Response;
};

type FetchSuccessResponse = {
  type: 'fetchSuccess';
  fetchResponse: Response;
};

type SchemaSuccessResponse<T> = {
  type: 'schemaSuccess';
  data: T;
  fetchResponse: Response;
};

export class FetchHTTPError extends Error {
  public response: Response;

  public constructor(message: string, response: Response) {
    super(message);

    this.response = response;
  }
}

export class FetchTypeCheckError extends Error {
  public response: Response;

  public json: unknown;

  public constructor(message: string, response: Response, json: unknown) {
    super(message);

    this.response = response;
    this.json = json;
  }
}

/**
 * This method works like native fetch but errors out if the response status isn't okay. This
 * is often the default behavior of third party http libraries such as axios and reduces the
 * boilerplate you need if there is no graceful way of handling the error.
 */
export const fetchWithErrorHandling = async (...params: FetchParams): Promise<Response> => {
  const response = await fetch(...params);

  if (!response.ok) {
    // We clone before getting the response text incase the user still wants to get the JSON value
    // after reading the error.
    const responseText = await response.clone().text();

    throw new FetchHTTPError(
      `${response.status} ${response.statusText}: ${responseText}`,
      response,
    );
  }

  return response;
};

/**
 * This method works like native fetch but with the JSON and response objects wrapped together
 * to reduce the biolerplate of common usage. This also sets the correct headers in the request
 * object unless overwritten. You can pass an optional third typecheck function which will throw
 * an error if the typecheck fails. This can be handy for throwing errors earlier in the callstack
 * when there are type errors from the server.
 */
export const fetchJSON = async <T>(
  url: FetchParams[0],
  providedOptions?: FetchParams[1],
  providedTypeGuard?: (x: unknown) => x is T,
): Promise<JSONResult<T>> => {
  const options = { ...(providedOptions ?? {}) };
  const headers = options.headers ?? {};
  options.headers = headers;
  const assumeServerResponseHasCorrectType = (_x: unknown): _x is T => true;
  const typeGuard = providedTypeGuard ?? assumeServerResponseHasCorrectType;

  const contentType: unknown = headers['Content-Type'];
  if (isString(contentType)) {
    headers['Content-Type'] = contentType;
  } else {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetchWithErrorHandling(url, options);

  // The as T is not necessarily safe but no more dangerous to what we were doing
  // with axios. We can explore typeguards if we get a lot of errors further downstream.
  const object: unknown = await response.json();

  if (!typeGuard(object)) {
    throw new FetchTypeCheckError(
      'failed type check when converting JSON to type',
      response,
      object,
    );
  }

  return {
    response,
    object,
  };
};

/**
 * This method works similarly to `fetchWithErrorHandling` but instead of throwing an error,
 * it returns a union of either a fetch error or the successful response.
 *
 * @param params - The parameters to pass to the fetch call
 * @returns A union of the possible errors or the successful response
 *
 * @example
 * const response = await fetchWithReturnedError('https://example.com/api/data');
 */
export const fetchWithReturnedError = async (
  ...params: FetchParams
): Promise<FetchErrorResponse | FetchSuccessResponse> => {
  const response = await tryCatchAsync({
    fn: async () => fetch(...params),
    errorFn: (error) => ({
      type: 'fetchError' as const,
      message: 'Error occurred while attempting to fetch data',
      error: error instanceof Error ? error : new Error('Unknown error'),
    }),
  });

  if (response.type === 'fetchError') {
    return response;
  }

  if (!response.ok) {
    // We clone before getting the response text incase the user still wants to get the JSON value
    // after reading the error.
    const responseText = await response.clone().text();

    return {
      type: 'fetchErrorWithResponse',
      message: `${response.status} ${response.statusText}: ${responseText}`,
      error: new FetchHTTPError(
        `${response.status} ${response.statusText}: ${responseText}`,
        response,
      ),
      fetchResponse: response,
    };
  }

  return { type: 'fetchSuccess', fetchResponse: response };
};

/**
 * This method works similarly to `fetchJSON` but instead, uses a zod schema to
 * parse the response body. Also, instead of throwing an error, it returns a
 * union of the possible errors or the successfully parsed data.
 *
 * @param url - The url to fetch from.
 * @param schema - The zod schema to parse the response body with.
 * @param options - The options to pass to the fetch call.
 *
 * @example
 * const response = await fetchWithSchema(
 *   'https://example.com/api/data',
 *   z.object({ message: z.string() }),
 * );
 */
export const fetchWithSchema = async <I extends z.ZodTypeAny>(
  url: FetchParams[0],
  schema: I,
  options?: FetchParams[1],
): Promise<
  FetchErrorResponse | SchemaErrorResponse | SchemaSuccessResponse<z.output<typeof schema>>
> => {
  const headers = match(options?.headers)
    .with(P.nullish, () => ({ 'Content-Type': 'application/json' }))
    .when(
      (currentHeaders) => !isString(currentHeaders['Content-Type']),
      (currentHeaders) => ({ ...currentHeaders, 'Content-Type': 'application/json' }), // eslint-disable-line @typescript-eslint/no-misused-spread
    )
    .otherwise(identity);

  const response = await fetchWithReturnedError(url, { ...options, headers });

  // The as T is not necessarily safe but no more dangerous to what we were doing
  // with axios. We can explore typeguards if we get a lot of errors further downstream.
  return match(response)
    .with({ type: 'fetchError' }, { type: 'fetchErrorWithResponse' }, identity)
    .with({ type: 'fetchSuccess' }, async ({ fetchResponse }) => {
      const json: unknown = await fetchResponse.json();

      const parsedData = schema.safeParse(json);

      return !parsedData.success
        ? {
            type: 'schemaError' as const,
            message: 'Failed to parse JSON with schema',
            error: parsedData.error,
            fetchResponse,
          }
        : {
            type: 'schemaSuccess' as const,
            // Using a type cast here is safe because we know the schema is valid.
            // The Zod docs also recommend this approach as a best practice:
            // https://zod.dev/?id=inferring-the-inferred-type - Burns
            data: parsedData.data as z.infer<typeof schema>,
            fetchResponse,
          };
    })
    .exhaustive();
};
